import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import useGetUser from '@/features/User/hooks/useGetUser';
import { ListSaved } from '../list';
import useGetSavedLists from './useGetSavedLists';

const useSaveList = (list: ListSaved) => {
	const utils = trpc.useUtils();
	const { t } = useTranslation();
	const { user } = useGetUser();
	const { listsSaved } = useGetSavedLists();

	const alreadySaved = listsSaved.some(
		(listSaved) => listSaved.public_uid === list?.public_uid
	);

	const { mutate, isPending } = trpc.list.save.useMutation({
		onMutate: async (listSaved) => {
			alreadySaved
				? toast.success(t('sharedList.delete.success'))
				: toast.success(t('sharedList.save.success'));

			await utils.user.get.cancel();

			const listSavedResponse = utils.list.getSaved.getData() || {
				status: 200,
				listsSaved: [],
			};

			const fakedList: ListSaved = {
				id: Math.random() * 100,
				fk_user: user!.id,
				name: list.name,
				public_uid: listSaved.public_uid,
				user: {
					firstname: 'John',
					lastname: 'Doe',
					birthday: '1990-01-01',
				},
				created_at: new Date().toISOString(),
				updated_at: new Date().toISOString(),
			};

			const optimisticLists = !alreadySaved
				? [...listSavedResponse.listsSaved, fakedList]
				: listSavedResponse.listsSaved.filter(
						(listSaved) => listSaved.public_uid !== list.public_uid
					);

			utils.list.getSaved.setData(undefined, () => {
				return { status: 200, listsSaved: optimisticLists };
			});

			return { listSavedResponse };
		},
		onSuccess: (response) => {
			const listSavedResponse = utils.list.getSaved.getData() || {
				status: 200,
				listsSaved: [],
			};

			utils.list.getSaved.setData(undefined, () => {
				if (response.action === 'created') {
					return {
						status: 200,
						listsSaved: listSavedResponse.listsSaved.map(
							(listSaved) => {
								if (list.public_uid === listSaved.public_uid) {
									return {
										...listSaved,
										user: response.user,
									};
								}
								return listSaved;
							}
						),
					};
				}
				return {
					status: 200,
					listsSaved: listSavedResponse.listsSaved.filter(
						(listSaved) => listSaved.public_uid !== list.public_uid
					),
				};
			});
		},
		onError(_, __, context) {
			if (!context?.listSavedResponse) return;
			utils.list.get.setData(undefined, () => {
				return {
					status: 200,
					lists: context?.listSavedResponse.listsSaved,
				};
			});
			toast.error(t('error.generic'));
		},
	});

	return {
		saveList: mutate,
		saveListLoading: isPending,
	};
};

export default useSaveList;
